import React, { useEffect } from 'react';
import { DatePicker } from '@mantine/dates';
import PrimaryButton from 'components/primaryButton';
import { Controller, useForm } from 'react-hook-form';
import {
  Avatar,
  Checkbox,
  Modal,
  MultiSelect,
  Select,
  Textarea,
  TextInput,
} from '@mantine/core';
import dayjs from 'dayjs';

import useGetEmployeesList from '../hooks/useGetEmployeesList';
import useCreateProject from '../hooks/useCreateProject';
import useUpdateProjectData from '../hooks/useUpdateProject';
import useGetAllProjectsMembers from '../hooks/useGetAllProjectMembers';
import useDeleteProjectMember from '../hooks/useDeleteProjectMember';
import useAddProjectMembers from '../hooks/useUpdateProjectMembersData';

import { ReactComponent as CalenderIcon } from 'assets/icons/calenderIcon.svg';
import { ReactComponent as DownArrow } from 'assets/icons/downArrow.svg';
import { ReactComponent as CrossIcon } from 'assets/icons/crossIcon.svg';

export default function CreateProject({
  isModalOpen,
  setIsModalOpen,
  projectDetails,
  name = 'Create',
}) {
  //   default value for form
  const defaultValues = {
    title: '',
    priority: '',
    from_date: '',
    to_date: '',
    is_active: true,
    lead_id: '',
    members: '',
    add_task_board: false,
    add_repository: false,
    add_chat_group: false,
    status: 'Not yet started',
    description: '',
  };

  const {
    handleSubmit,
    formState: { errors },
    watch,
    control,
    reset,
    setValue,
    getValues,
  } = useForm({ defaultValues });

  // function to handle modal close
  const closeModal = () => {
    setIsModalOpen(false);
    reset();
  };

  const getEmployeesList = useGetEmployeesList();
  const createProjectMutation = useCreateProject(closeModal);
  const updateProjectMutation = useUpdateProjectData(closeModal);
  const getAllProjectMembers = useGetAllProjectsMembers(projectDetails?.id);
  const deleteProjectMemberMutation = useDeleteProjectMember();
  const addProjectMember = useAddProjectMembers();

  // error message
  const getErrorMessage = (name) => {
    return (
      errors[name] && (
        <p className=' text-sm font-semibold text-red-500'>
          {errors[name]?.message}
        </p>
      )
    );
  };

  //   handle form submit
  const onSubmit = (data) => {
    // validating and removing if the members array includes project lead
    let finalMembers = [];
    data?.members?.map((item) => {
      if (item !== data?.lead_id) {
        finalMembers.push(item);
      }
      return true;
    });
    console.log('finalMembers:', finalMembers);
    data['members'] = finalMembers;
    data['from_date'] = dayjs(data?.from_date).format('YYYY-MM-DD');
    data['to_date'] = dayjs(data?.to_date).format('YYYY-MM-DD');
    if (data?.id) {
      delete data?.members;
      updateProjectMutation?.mutate(data);
    } else {
      createProjectMutation?.mutate(data);
    }
  };

  //   custom dropdown item for select
  const SelectItem = ({
    activeStatus,
    value,
    label,
    image,
    name,
    description,
    ...others
  }) => {
    return (
      <div className={`flex px-5 `}>
        <div
          {...others}
          className={`flex items-center py-2 cursor-pointer  ${
            activeStatus ? '' : 'opacity-60'
          } `}
        >
          <Avatar src={image} color='red'>
            {label[0]?.toUpperCase()}
          </Avatar>
          <div className='pl-1 '>
            <p className='text-18 '>{label} </p>
            <p className='text-xs text-gray3 pt-1 '>{description} </p>
          </div>
        </div>

        <p className='ml-auto'>
          {activeStatus ? (
            <span className='ml-3 text-[12px] text-[#28B446] bg-[#c1f3cc] py-1 px-2 rounded-lg '>
              Active
            </span>
          ) : (
            <span className='ml-3 text-[12px] text-[#D0342C] bg-[#f7bab6] py-1 px-2 rounded-lg'>
              Inactive
            </span>
          )}
        </p>
      </div>
    );
  };

  //   dropdown values for team member multi select input
  const getDropdownData = () => {
    const dataArray = [];
    getEmployeesList?.data?.data?.map((employee) => {
      if (~~watch('lead_id') !== employee.id) {
        dataArray.push({
          activeStatus: employee?.user__is_active,
          image: employee?.profile_picture,
          label: employee.user__first_name,
          value: `${employee.id}`,
          description: employee?.designation__name,
          disabled: employee?.user__is_active ? false : true,
        });
      }
      return true;
    });
    return dataArray;
  };

  // useEffect to reset values for edit form
  useEffect(() => {
    if (projectDetails) {
      const checkValue = projectDetails?.members?.map(
        (item) => `${item?.employee__id}`
      );
      if (projectDetails) {
        // setProjectMembers(checkValue);
        reset({
          id: projectDetails?.id,
          title: projectDetails?.title,
          priority: projectDetails?.priority,
          from_date: new Date(projectDetails?.from_date),
          to_date: new Date(projectDetails?.to_date),
          is_active: projectDetails?.is_active,
          lead_id: `${projectDetails?.lead}`,
          members: checkValue,
          add_task_board: projectDetails?.add_task_board,
          add_repository: projectDetails?.add_repository,
          add_chat_group: projectDetails?.add_chat_group,
          status: projectDetails?.status,
          description: projectDetails?.description,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDetails]);

  const watchFromDate = watch('from_date');
  const watchToDate = watch('to_date');

  useEffect(() => {
    if (watchFromDate && watchToDate) {
      const date1 = dayjs(dayjs(watchToDate).format('YYYY-MM-DD'));
      const date2 = dayjs(dayjs(watchFromDate).format('YYYY-MM-DD'));
      if (date1.diff(date2, 'day') < 0) {
        reset({
          ...getValues(),
          to_date: null,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchFromDate, watchToDate]);

  useEffect(() => {
    if (projectDetails) {
      let finalData = [];
      getAllProjectMembers?.data?.data?.map((item) => {
        finalData.push(`${item?.employee_id}`);
        return true;
      });
      setValue('members', finalData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAllProjectMembers?.data?.data]);

  const changeHandler = (data) => {
    if (getAllProjectMembers?.data?.data?.length > data?.length) {
      const removedEmployee = getAllProjectMembers?.data?.data?.filter(
        (member) => data.indexOf(`${member?.employee_id}`)
      );
      deleteProjectMemberMutation?.mutate({ id: removedEmployee[0]?.id });
    } else {
      if (data?.length > getAllProjectMembers?.data?.data?.length) {
        addProjectMember.mutate({
          project_id: projectDetails?.id,
          employee_id: data[data?.length - 1],
        });
      }
    }
  };

  return (
    <div>
      <Modal
        opened={isModalOpen}
        onClose={() => closeModal(false)}
        title={``}
        withCloseButton={false}
        overlayColor={'#F8F7F799'}
        overlayBlur={20}
        overlayOpacity={0.5}
        centered
        size='xl'
        radius='md'
        overflow='inside'
        transitionDuration={100}
        classNames={{
          title: 'w-full text-333333 font-semibold text-xl text-center',
          modal: 'pl-10',
          body: 'scroller -mr-4 pr-2',
        }}
      >
        <div className='pb-6 sticky top-0 bg-white z-20'>
          <CrossIcon
            className='w-10 ml-auto cursor-pointer absolute top-0 right-0'
            onClick={() => closeModal()}
          />
          <p className='w-full text-333333 font-semibold text-xl text-center pt-1'>
            {name} a project
          </p>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='flex flex-wrap'>
            <div className='w-1/2 py-2 pr-4'>
              <Controller
                control={control}
                name='title'
                rules={{ required: 'required' }}
                render={({ field }) => (
                  <TextInput
                    label='Project title*'
                    {...field}
                    size='md'
                    classNames={{
                      input: 'border-primary h-12',
                      label: 'text-neutralsGrey text-sm',
                    }}
                  />
                )}
              />
              {getErrorMessage('title')}
            </div>
            <div className='w-1/2 py-2 px-4'>
              <Controller
                control={control}
                name='priority'
                rules={{ required: 'required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    size='md'
                    label='Priority*'
                    data={[
                      { value: 'Low', label: 'Low' },
                      { value: 'Medium', label: 'Medium' },
                      { value: 'High', label: 'High' },
                    ]}
                    rightSection={<DownArrow />}
                    styles={{ rightSection: { pointerEvents: 'none' } }}
                    classNames={{
                      input: 'border-primary truncate h-12 ',
                      label: 'text-neutralsGrey text-sm',
                    }}
                  />
                )}
              />
              {getErrorMessage('priority')}
            </div>
            <div className='w-1/2 py-2 pr-4'>
              <Controller
                control={control}
                name='from_date'
                rules={{ required: 'required' }}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    label='Start date*'
                    size='md'
                    rightSection={<CalenderIcon className='w-5 mr-2' />}
                    styles={{ rightSection: { pointerEvents: 'none' } }}
                    classNames={{
                      input: 'border-primary h-12',
                      label: 'text-neutralsGrey text-sm',
                      yearPickerControlActive: 'text-white bg-primary',
                      monthPickerControlActive: 'text-white bg-primary',
                    }}
                  />
                )}
              />
              {getErrorMessage('from_date')}
            </div>
            <div className='w-1/2 py-2 px-4'>
              <Controller
                control={control}
                name='to_date'
                rules={{ required: 'required' }}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    label='End date*'
                    size='md'
                    rightSection={<CalenderIcon className='w-5 mr-2' />}
                    styles={{ rightSection: { pointerEvents: 'none' } }}
                    minDate={dayjs(new Date(watchFromDate)).toDate()}
                    classNames={{
                      input: 'border-primary h-12',
                      label: 'text-neutralsGrey text-sm',
                      yearPickerControlActive: 'text-white bg-primary',
                      monthPickerControlActive: 'text-white bg-primary',
                    }}
                  />
                )}
              />
              {getErrorMessage('to_date')}
            </div>
            <div className='w-full py-2 pr-4'>
              <Controller
                control={control}
                name='lead_id'
                rules={{ required: 'required' }}
                render={({ field }) => (
                  <Select
                    {...field}
                    size='md'
                    itemComponent={SelectItem}
                    label='Project lead / manager*'
                    placeholder='Search or select'
                    searchable
                    data={
                      !getEmployeesList?.data?.data?.length
                        ? []
                        : getEmployeesList?.data?.data?.map((employee) => {
                            return {
                              image: employee?.profile_picture,
                              label: employee.user__first_name,
                              value: `${employee.id}`,
                              description: employee?.designation__name,
                              activeStatus: employee?.user__is_active,
                              disabled: employee?.user__is_active
                                ? false
                                : true,
                            };
                          })
                    }
                    rightSection={<DownArrow />}
                    styles={{ rightSection: { pointerEvents: 'none' } }}
                    classNames={{
                      input: 'border-primary truncate h-12 ',
                      label: 'text-neutralsGrey text-sm',
                    }}
                  />
                )}
              />
              {getErrorMessage('lead_id')}
            </div>

            {/*condition for different operations for project members on edit and add projects */}
            {name === 'Edit' ? (
              <div className='w-full py-2 pr-4'>
                <Controller
                  control={control}
                  name='members'
                  rules={{ required: 'required' }}
                  render={({ field }) => (
                    <MultiSelect
                      {...field}
                      size='md'
                      itemComponent={SelectItem}
                      label='Assign members*'
                      placeholder='Search or select'
                      searchable
                      onChange={(data) => changeHandler(data)}
                      data={
                        !getEmployeesList?.data?.data?.length
                          ? []
                          : getDropdownData()
                      }
                      rightSection={<DownArrow />}
                      styles={{ rightSection: { pointerEvents: 'none' } }}
                      classNames={{
                        input: 'border-primary truncate min-h-[7vh]',
                        label: 'text-neutralsGrey text-sm',
                      }}
                    />
                  )}
                />
                {getErrorMessage('members')}
              </div>
            ) : (
              <div className='w-full py-2 pr-4'>
                <Controller
                  control={control}
                  name='members'
                  rules={{ required: 'required' }}
                  render={({ field }) => (
                    <MultiSelect
                      {...field}
                      size='md'
                      itemComponent={SelectItem}
                      label='Assign members*'
                      placeholder='Search or select'
                      searchable
                      data={
                        !getEmployeesList?.data?.data?.length
                          ? []
                          : getDropdownData()
                      }
                      rightSection={<DownArrow />}
                      styles={{ rightSection: { pointerEvents: 'none' } }}
                      classNames={{
                        input: 'border-primary truncate min-h-[7vh]',
                        label: 'text-neutralsGrey text-sm',
                      }}
                    />
                  )}
                />
                {getErrorMessage('members')}
              </div>
            )}

            <div className='w-full mt-4 mr-4'>
              <Controller
                control={control}
                rules={{ required: 'required' }}
                name='description'
                render={({ field }) => (
                  <Textarea
                    {...field}
                    label='Add a brief / description about project*'
                    size='sm'
                    classNames={{
                      input:
                        ' text-[15px] bg-transparent border-[#FB7C51]  text-normal rounded pl-3 h-20 scroller',
                      label: 'text-[#8E8E8E] text-sm font-normal ',
                    }}
                  />
                )}
              />
              {getErrorMessage('description')}
            </div>
            {name === 'Edit' ? null : (
              <div className='w-full flex flex-col items-center mt-4'>
                <div className='flex w-full space-x-10'>
                  <div classname='flex w-1/2'>
                    <Controller
                      control={control}
                      name='add_task_board'
                      render={({ field }) => (
                        <Checkbox
                          {...field}
                          label='Create task board for this project'
                          classNames={{ label: 'text-neutralsGrey text-sm' }}
                        />
                      )}
                    />
                  </div>
                  <div classname='flex w-1/2'>
                    <Controller
                      control={control}
                      name='add_repository'
                      render={({ field }) => (
                        <Checkbox
                          {...field}
                          label='Create repository for this project'
                          classNames={{ label: 'text-neutralsGrey text-sm' }}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className='flex w-full space-x-10 mt-4'>
                  <div classname='flex w-1/2 '>
                    <Controller
                      control={control}
                      name='add_chat_group'
                      render={({ field }) => (
                        <Checkbox
                          {...field}
                          label='Create chat group for this project'
                          classNames={{ label: 'text-neutralsGrey text-sm' }}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className='flex justify-center mt-8 mb-2'>
            <button className='w-32'>
              <PrimaryButton
                isLoading={
                  createProjectMutation?.isLoading ||
                  updateProjectMutation?.isLoading
                }
              >
                Save
              </PrimaryButton>
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
